import { useEffect, useId, useMemo, useRef, useState } from 'react';

export type SearchableSelectGroup = {
  label: string;
  options: { label: string; value: string }[];
};

type SearchableSelectProps = {
  id?: string;
  label?: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  groups: SearchableSelectGroup[];
  placeholder?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;
  error?: string;
  disabled?: boolean;
};

const filterGroups = (groups: SearchableSelectGroup[], query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return groups;

  return groups
    .map((group) => ({
      ...group,
      options: group.options.filter(
        (option) =>
          option.label.toLowerCase().includes(normalizedQuery) ||
          option.value.toLowerCase().includes(normalizedQuery),
      ),
    }))
    .filter((group) => group.options.length > 0);
};

export const SearchableSelect = ({
  id,
  label,
  required,
  value,
  onChange,
  groups,
  placeholder = 'Выберите',
  searchPlaceholder = 'Поиск...',
  loading = false,
  loadingText = 'Загрузка...',
  emptyText = 'Ничего не найдено',
  error,
  disabled = false,
}: SearchableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const allOptions = useMemo(() => groups.flatMap((group) => group.options), [groups]);

  const selectedLabel = allOptions.find((option) => option.value === value)?.label;

  const filteredGroups = useMemo(() => filterGroups(groups, searchQuery), [groups, searchQuery]);

  const hasOptions = filteredGroups.some((group) => group.options.length > 0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const toggleOpen = () => {
    if (disabled || loading) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className="w-full" ref={dropdownRef}>
      {label && (
        <label htmlFor={selectId} className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && '*'}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          id={selectId}
          disabled={disabled || loading}
          onClick={toggleOpen}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
          } ${disabled || loading ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          <span className={`truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-500'}`}>
            {loading ? loadingText : selectedLabel || placeholder}
          </span>
          <svg
            className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-lg">
            <div className="sticky top-0 border-b border-gray-200 bg-white p-2">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={(event) => event.stopPropagation()}
              />
            </div>

            <div className="max-h-60 overflow-auto" role="listbox">
              {!hasOptions && <p className="px-3 py-2 text-sm text-gray-500">{emptyText}</p>}
              {filteredGroups.map((group) => (
                <div key={group.label}>
                  <div className="px-3 py-1.5 text-xs font-semibold uppercase text-gray-500">
                    {group.label}
                  </div>
                  {group.options.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={value === option.value}
                      onClick={() => handleSelect(option.value)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                        value === option.value ? 'bg-blue-50 text-blue-700' : ''
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};
